import { config } from '@/config/env'

/**
 * Cliente HTTP del sistema. Un solo lugar donde se pone el token, se normaliza
 * el error y se maneja el 401.
 *
 * Antes había tres implementaciones casi iguales —`ApiService.request`, el
 * `request` de `services/api.ts` y `apiRequest` de `apiUtils`— más llamadas
 * sueltas a `fetch` y a `axios` repartidas por las pantallas, y 84 lugares
 * leyendo el token de `localStorage` a mano. Cualquier cambio en la sesión
 * había que repetirlo en todos.
 *
 * Las tres capas anteriores siguen existiendo con la misma firma para no tocar
 * los cientos de lugares que las usan: ahora delegan aquí.
 */

export interface HttpError {
  message: string | string[]
  error: string
  /** Código HTTP. 0 si ni siquiera se pudo llegar al servidor. */
  statusCode: number
  /** Mismo valor que statusCode; algunas pantallas leen uno y otras el otro. */
  status: number
  response?: {
    data: unknown
    status: number
  }
}

export interface HttpOptions extends RequestInit {
  /**
   * Cuál token mandar. Por omisión se deduce de la ruta actual, que es lo que
   * hacían las capas anteriores.
   *
   * Hace falta poder decirlo cuando el endpoint exige uno en concreto: hay
   * pantallas que sirven al portal y al panel interno con el mismo componente,
   * y ahí la ruta no basta para adivinarlo.
   */
  token?: 'staff' | 'proveedor'
  /** Devolver el cuerpo como Blob en vez de convertirlo. Para descargas. */
  comoBlob?: boolean
  /**
   * No cerrar la sesión ni redirigir si la respuesta es 401.
   *
   * Hace falta en dos casos: el intento de login, donde un 401 significa
   * credenciales incorrectas y no sesión expirada, y las consultas que sondean
   * un permiso y esperan que pueda fallar.
   */
  skipAuthRedirect?: boolean
}

/** El portal del proveedor usa su propio token. */
const esRutaDeProveedor = () =>
  window.location.pathname.startsWith('/proveedores')

/** Qué sesión usa una petición: la explícita, o la que toca por la ruta. */
const sesionDe = (cual?: 'staff' | 'proveedor'): 'staff' | 'proveedor' =>
  cual ?? (esRutaDeProveedor() ? 'proveedor' : 'staff')

const llaveDelToken = (sesion: 'staff' | 'proveedor') =>
  sesion === 'proveedor' ? 'supplier_access_token' : 'access_token'

const tokenActual = (cual?: 'staff' | 'proveedor'): string | null =>
  localStorage.getItem(llaveDelToken(sesionDe(cual)))

/**
 * Cierra la sesión que falló, y solo esa.
 *
 * Antes borraba los dos tokens: un 401 de un endpoint interno sacaba también al
 * proveedor y al revés. Las dos sesiones son independientes —se puede tener el
 * portal abierto en una pestaña y el sistema en otra—, así que tirar la que no
 * falló cerraba sesiones sin motivo.
 */
/**
 * Motivo del último cierre de sesión, para que la pantalla de login lo diga.
 *
 * Se guarda en `sessionStorage` porque el redirect recarga la página y con eso
 * se pierde cualquier estado en memoria: sin esto, al proveedor lo sacaba al
 * login sin ninguna explicación.
 */
export const LLAVE_MOTIVO_CIERRE = 'motivo_cierre_sesion'

/**
 * Rastro del último cierre de sesión por 401, para poder diagnosticarlo después.
 *
 * Sobrevive a la recarga a propósito: cuando la sesión se cae, la página se
 * recarga y con eso se pierde la consola y cualquier estado en memoria, así que
 * no queda de qué agarrarse para saber qué petición falló.
 */
export const LLAVE_RASTRO_CIERRE = 'rastro_cierre_sesion'

const cerrarSesion = (
  mensaje: string,
  sesion: 'staff' | 'proveedor',
  rastro?: { endpoint: string; status: number },
) => {
  localStorage.removeItem(llaveDelToken(sesion))
  try {
    sessionStorage.setItem(LLAVE_MOTIVO_CIERRE, mensaje)
    localStorage.setItem(
      LLAVE_RASTRO_CIERRE,
      JSON.stringify({
        sesion,
        mensaje,
        endpoint: rastro?.endpoint,
        status: rastro?.status,
        ruta: window.location.pathname,
        cuando: new Date().toISOString(),
      }),
    )
  } catch {
    // Modo privado o almacenamiento lleno: el aviso se pierde, nada más.
  }

  window.dispatchEvent(
    new CustomEvent('auth:unauthorized', { detail: { message: mensaje, sesion } }),
  )

  // El pequeño retraso deja que quien escucha el evento limpie su estado antes
  // de que la página cambie.
  setTimeout(() => {
    const login = sesion === 'proveedor' ? '/proveedores/login' : '/login'
    // Solo se manda al login si la sesión que murió es la de esta pantalla: si
    // no, la pantalla actual sigue siendo válida.
    const enSuPortal = sesion === 'proveedor' ? esRutaDeProveedor() : !esRutaDeProveedor()
    if (enSuPortal && !window.location.pathname.includes('/login')) {
      window.location.href = login
    }
  }, 100)
}

const construirError = (
  status: number,
  datosCrudos: { message?: string | string[]; mensaje?: string; error?: string },
): HttpError => {
  // Los bloqueos 422 del portal del proveedor (`expediente_vencido`, el de
  // facturación...) mandan su texto en `mensaje`. Sin esto, cada pantalla que
  // lee `message` caía a su "Error al ..." genérico y el proveedor no se
  // enteraba del motivo. Se copia sin quitar `mensaje`, que otros leen.
  const datos =
    !datosCrudos.message && typeof datosCrudos.mensaje === 'string' && datosCrudos.mensaje
      ? { ...datosCrudos, message: datosCrudos.mensaje }
      : datosCrudos

  return {
    message: datos.message || 'Ocurrió un error en la solicitud.',
    error: datos.error || `HTTP ${status}`,
    statusCode: status,
    status,
    response: { data: datos, status },
  }
}

/**
 * Arma la cadena de consulta omitiendo lo que venga vacío, que es lo que hacía
 * `axios` con su opción `params`.
 */
export const construirQuery = (
  params: Record<string, string | number | boolean | undefined | null>,
): string => {
  const busqueda = new URLSearchParams()
  for (const [clave, valor] of Object.entries(params)) {
    if (valor !== undefined && valor !== null && valor !== '') {
      busqueda.append(clave, String(valor))
    }
  }
  const query = busqueda.toString()
  return query ? `?${query}` : ''
}

/**
 * Hace la petición y devuelve el cuerpo ya convertido.
 *
 * `endpoint` puede ser una ruta (`/almacen/herramientas`) o una URL completa,
 * porque las capas que delegan aquí usan las dos formas.
 *
 * Devuelve `undefined` cuando la respuesta no trae cuerpo —un 204, o un 200
 * vacío— en vez de reventar al intentar convertirlo. Ese detalle ya había
 * causado un bug: eliminar un proyecto respondía sin cuerpo, el `json()`
 * lanzaba, y la pantalla nunca se refrescaba.
 */
export const httpRequest = async <T>(
  endpoint: string,
  options: HttpOptions = {},
): Promise<T | undefined> => {
  const { skipAuthRedirect, token: cualToken, comoBlob, ...init } = options

  // Hay servicios que arman la URL completa con `API_URL` antes de llamar aquí.
  // Si no se detecta, la base se pone dos veces. Con una base absoluta se
  // notaba por el `http`, pero con una base relativa —el proxy local
  // `/api-proxy/api`— no, y salía una URL como
  // `/api-proxy/api/api-proxy/api/internal/...`.
  const yaTraeLaBase =
    endpoint === config.apiBaseUrl ||
    endpoint.startsWith(`${config.apiBaseUrl}/`)

  const url =
    endpoint.startsWith('http') || yaTraeLaBase
      ? endpoint
      : `${config.apiBaseUrl}${endpoint}`

  // Con FormData el navegador pone el Content-Type con su boundary; ponerlo a
  // mano rompe la subida de archivos.
  const esFormData = init.body instanceof FormData
  const token = tokenActual(cualToken)

  let response: Response
  try {
    response = await fetch(url, {
      ...init,
      headers: {
        ...(!esFormData && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...init.headers,
      },
    })
  } catch (err) {
    throw construirError(0, {
      message: 'Error de conexión. Verifica tu conexión a internet.',
      error: err instanceof Error ? err.message : 'Network Error',
    })
  }

  if (!response.ok) {
    const datos = await response.json().catch(() => ({}))

    if (response.status === 401 && !skipAuthRedirect) {
      cerrarSesion(
        (typeof datos.message === 'string' && datos.message) ||
          'Tu sesión ha expirado. Inicia sesión nuevamente.',
        sesionDe(cualToken),
        { endpoint: url.replace(config.apiBaseUrl, ''), status: response.status },
      )
    }

    throw construirError(response.status, datos)
  }

  if (comoBlob) return (await response.blob()) as T

  if (response.status === 204) return undefined

  const texto = await response.text()
  if (!texto) return undefined

  try {
    return JSON.parse(texto) as T
  } catch {
    // 2xx con un cuerpo que no es JSON (por ejemplo "OK").
    return undefined
  }
}
